import { Injectable } from '@angular/core';
import { Config } from '../app.configs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AuthService } from './auth.service';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { PaginatedTransactions } from '../interfaces/paginated-transaction.interface';
import { CacheService } from './cache.service';

export interface NoahRefund {
  refund_id: string;
  amount: string;
  fiat_currency: string;
  status: string;
  requested_time: string;
  created_at: string;
}

export interface NoahDepositRefund {
  uuid: string;
  status: string;
  fiat_amount: string;
  fiat_currency: string;
  reference: string;
  sender_full_name: string;
  sender_iban: string;
  sender_bic: string;
  created_at: string;
  refunds: NoahRefund[];
}

export interface PaginatedNoahDepositRefunds {
  count: number;
  results: NoahDepositRefund[];
}

@Injectable({
  providedIn: 'root',
})
export class TransactionService {
  private readonly CACHE_PREFIX = 'transactions:';

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private cacheService: CacheService,
  ) {}

  getTransactions(
    page: number = 1,
    search: string = '',
    transactionType: string = '',
    transactionStatus: string = '',
    accountingType: string = '',
    forceRefresh: boolean = false,
    activeRfi: boolean = false,
    paymentMethod: string = '',
  ): Observable<PaginatedTransactions> {
    const cacheKey = `${this.CACHE_PREFIX}${page}:${search}:${transactionType}:${transactionStatus}:${accountingType}:${activeRfi}:${paymentMethod}`;

    return this.cacheService.get<PaginatedTransactions>(
      cacheKey,
      () => {
        let params = new HttpParams()
          .set('page', page)
          .set('search', search)
          .set('transaction_type', transactionType)
          .set('accounting_type', accountingType)
          .set('transaction_status', transactionStatus)
          .set('payment_method', paymentMethod);

        if (activeRfi) {
          params = params
            .set('rfi_received_at', 'true')
            .set('rfi_completed_at', 'false')
            .set('rfi_failed_at', 'false');
        }

        const headers = this.authService.getHeaders();
        return this.http.get<PaginatedTransactions>(
          Config.toApiUrl(`dashboard/transactions/`),
          { headers, params, withCredentials: true },
        );
      },
      undefined,
      forceRefresh,
    );
  }

  invalidateTransactionsCache(): void {
    this.cacheService.invalidateByPrefix(this.CACHE_PREFIX);
  }

  convertToEurc(uuid: string, amount = 0): Observable<any> {
    const headers = this.authService.getHeaders();

    const payload = {
      amount: amount,
    };

    return this.http
      .post(
        Config.toApiUrl(`dashboard/users/${uuid}/convert-to-eurc/`),
        payload,
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateTransactionsCache()));
  }

  getFailedNoahDeposits(userUuid: string): Observable<PaginatedNoahDepositRefunds> {
    const headers = this.authService.getHeaders();
    return this.http.get<PaginatedNoahDepositRefunds>(
      Config.toApiUrl(`dashboard/users/${userUuid}/noah-deposit-refunds/`),
      { headers, withCredentials: true },
    );
  }
}
